import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Icon,
  TYPE_OUTLINE
} from '@mds/mds-reactjs-library';
import Outline24ERemove from '@mds/mds-icons/icons/svg/outline-24-e-remove.svg';
import {
  RELEASE_INFO_MENU_ITEMS,
  getReleaseInfoMenuIndexForPublication,
  getReleaseInfoMenuIndexForDisplayName,
  getReleaseInfoMenuItemDisplayLabel
} from '../../Constants/ReleaseDisplayNames';
import {
  FullGranularityGeographicRegions,
  FortyTwoMarketGeographicRegions,
  FortyTwoMarketPreviousGeographicRegions,
  FortyTwoMarketGeographicRegionsSegmentLatest,
  FullGranularityLatestCountryDetails,
  FullGranularityPreviousCountryDetails,
  FullGranularityPreviousReleaseGeographicRegions,
  SegmentGranularityLatestCountryDetails,
  SegmentGranularityPreviousCountryDetails,
  GgcLatestAumReleaseInfoBlock,
  GgcPreviousReleaseInfoBlock,
  INTRO_29_MARKETS,
  INTRO_6_MARKETS,
  INTRO_42_AUM_NET_FLOWS,
  INTRO_LATEST_AUM_NET_FLOWS,
  INTRO_42_MARKETS_UPDATED,
  METRICS_FULL_GRANULARITY,
  METRICS_PREVIOUS_FULL_GRANULARITY,
  METRICS_SEGMENT,
  METRICS_PREVIOUS_SEGMENT,
  METRICS_TFA,
  OverviewBankingPoolsContent,
  OverviewFullGranularityContent,
  OverviewFullGranularityPreviousReleaseContent,
  OverviewSegmentGranularityContent,
  OverviewSegmentGranularityPreviousReleaseContent,
  OverviewTotalFinancialAssetsContent,
  OverviewTotalFinancialAssetsPreviousReleaseContent,
  OverviewUsBankingPoolsDefaultContent,
  TfaLatestReleaseInfoBlock,
  TfaPreviousCountryDetails,
} from './DatasetInfoPopUpContent';

function formatPublicationCreatedAt(createdDate) {
  if (createdDate == null || createdDate === '') return null;
  const d = new Date(createdDate);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'medium' });
}

function publicationMatchesReleaseSection(publication, section) {
  const status = (publication?.publicationStatus?.name ?? '').toLowerCase();
  if (section === 'previous') return status === 'previous release';
  return status !== 'previous release';
}

const DatasetInfoPopUp = ({
  setShowDatasetInfo,
  datasets,
  allPublications,
  datasetId,
  selectedPublicationId,
  publicationByDataset = {},
  isGgcAdmin,
  releaseDisplayName,
  onApplyPublicationOnClose,
}) => {
  const TAB_OVERVIEW = 'overview';
  const TAB_RELEASE = 'release';
  const TAB_VERSIONS = 'versions';
  const [activeDatasetId, setActiveDatasetId] = useState(null);
  const [activeReleaseItemIdx, setActiveReleaseItemIdx] = useState(0);
  const [activeTab, setActiveTab] = useState(TAB_OVERVIEW);
  const openingPublicationIdRef = useRef(selectedPublicationId);
  const [pendingPublicationId, setPendingPublicationId] = useState(() => selectedPublicationId ?? null);

  const visibleReleaseMenuItems = useMemo(() => {
    const datasetBaseNames = new Set(
      (datasets || []).map((d) => (d.displayName ?? d.name ?? '').toLowerCase())
    );
    const filtered = RELEASE_INFO_MENU_ITEMS.filter((item) => datasetBaseNames.has(item.baseName));
    return filtered.map((item, idx) => {
      const prev = filtered[idx - 1];
      const showSectionHeading = idx === 0 || prev?.section !== item.section;
      return {
        ...item,
        sectionHeading: showSectionHeading ? item.sectionHeading : null
      };
    });
  }, [datasets]);

  const getVisibleMenuIndex = (section, baseName) => {
    return visibleReleaseMenuItems.findIndex(
      (item) => item.section === section && item.baseName === baseName
    );
  };

  useEffect(() => {
    if (activeReleaseItemIdx >= visibleReleaseMenuItems.length) {
      setActiveReleaseItemIdx(0);
    }
  }, [activeReleaseItemIdx, visibleReleaseMenuItems.length]);

  // Sync left-nav highlight + dataset from the publication the user opened (internal + GGC admin).
  useEffect(() => {
    if (selectedPublicationId != null && allPublications?.length) {
      const pub = allPublications.find(
        (p) => String(p.publicationId ?? p.id) === String(selectedPublicationId)
      );
      if (pub) {
        const idxFromPub = getReleaseInfoMenuIndexForPublication(pub);
        if (idxFromPub >= 0) {
          const menuItem = RELEASE_INFO_MENU_ITEMS[idxFromPub];
          const visibleIdx = menuItem
            ? getVisibleMenuIndex(menuItem.section, menuItem.baseName)
            : -1;
          if (visibleIdx >= 0) {
            setActiveReleaseItemIdx(visibleIdx);
            return;
          }
        }
      }
    }
    if (!isGgcAdmin) {
      if (releaseDisplayName) {
        const idxByLabel = getReleaseInfoMenuIndexForDisplayName(releaseDisplayName);
        if (idxByLabel >= 0) {
          const menuItem = RELEASE_INFO_MENU_ITEMS[idxByLabel];
          const visibleIdx = menuItem
            ? getVisibleMenuIndex(menuItem.section, menuItem.baseName)
            : -1;
          if (visibleIdx >= 0) {
            setActiveReleaseItemIdx(visibleIdx);
            return;
          }
        }
      }
      if (datasetId != null) {
        const matchedDataset = datasets.find((item) => item.datasetId === datasetId);
        if (matchedDataset) {
          const idx = visibleReleaseMenuItems.findIndex(
            (item) => item.baseName === matchedDataset.displayName?.toLowerCase()
          );
          if (idx >= 0) setActiveReleaseItemIdx(idx);
        }
      }
    }
  }, [
    isGgcAdmin,
    datasetId,
    datasets,
    releaseDisplayName,
    selectedPublicationId,
    allPublications,
    visibleReleaseMenuItems
  ]);

  // Keep versions/overview aligned with the selected release menu row (dataset + latest vs previous).
  useEffect(() => {
    const item = visibleReleaseMenuItems[activeReleaseItemIdx];
    if (item && datasets?.length) {
      const ds = datasets.find(
        (d) => (d.displayName ?? d.name ?? '').toLowerCase() === item.baseName
      );
      if (ds?.datasetId != null) {
        setActiveDatasetId(ds.datasetId);
        return;
      }
    }
    if (datasetId != null) {
      setActiveDatasetId(datasetId);
    }
  }, [activeReleaseItemIdx, datasets, datasetId, visibleReleaseMenuItems]);

  const activeReleaseItem = visibleReleaseMenuItems[activeReleaseItemIdx];
  const effectiveBaseName = activeReleaseItem?.baseName;
  const activeDatasetDisplayName = useMemo(() => {
    if (!activeDatasetId || !datasets?.length) return null;
    const ds = datasets.find((d) => (d.datasetId ?? d.id) === activeDatasetId);
    return ds?.displayName ?? ds?.name ?? null;
  }, [activeDatasetId, datasets]);
  const effectiveHeading = isGgcAdmin
    ? (activeDatasetDisplayName ?? activeReleaseItem?.label ?? '')
    : (activeReleaseItem
        ? getReleaseInfoMenuItemDisplayLabel(activeReleaseItem, allPublications)
        : '');
  const effectiveReleaseSection = activeReleaseItem?.section ?? 'latest';

  const publicationsBySelectedDatasetId = useMemo(() => {
    if (!activeDatasetId || !allPublications?.length) return [];
    return allPublications
      .filter((publication) => (publication.dataset?.datasetId ?? publication.datasetId) === activeDatasetId)
      .sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));
  }, [activeDatasetId, allPublications]);

  const versionsToShow = useMemo(
    () =>
      publicationsBySelectedDatasetId.filter((p) =>
        publicationMatchesReleaseSection(p, effectiveReleaseSection)
      ),
    [publicationsBySelectedDatasetId, effectiveReleaseSection]
  );

  useEffect(() => {
    if (!versionsToShow.length) {
      setPendingPublicationId(null);
      return;
    }
    setPendingPublicationId((prev) => {
      const ids = new Set(versionsToShow.map((v) => String(v.publicationId ?? v.id)));
      const prevStr = prev != null ? String(prev) : '';
      const storedForDataset = activeDatasetId != null ? publicationByDataset?.[activeDatasetId] : null;
      if (storedForDataset?.publicationId != null && ids.has(String(storedForDataset.publicationId))) {
        return storedForDataset.publicationId;
      }
      if (prevStr && ids.has(prevStr)) return prev;
      if (
        activeDatasetId != null &&
        selectedPublicationId != null &&
        ids.has(String(selectedPublicationId))
      ) {
        const selectedPub = allPublications?.find(
          (p) => String(p.publicationId ?? p.id) === String(selectedPublicationId)
        );
        const selectedPubDatasetId = selectedPub?.dataset?.datasetId ?? selectedPub?.datasetId;
        if (String(selectedPubDatasetId) === String(activeDatasetId)) {
          return selectedPublicationId;
        }
      }
      return versionsToShow[0].publicationId ?? versionsToShow[0].id;
    });
  }, [versionsToShow, selectedPublicationId, activeDatasetId, publicationByDataset, allPublications]);

  const handleClose = () => {
    setShowDatasetInfo(false);
  };

  /**
   * Apply a version pick immediately so the dataset loads; stay on the Versions tab.
   */
  const handleVersionPicked = (publicationId) => {
    if (publicationId == null) return;
    setPendingPublicationId(publicationId);
    if (typeof onApplyPublicationOnClose === 'function') {
      onApplyPublicationOnClose(publicationId, activeDatasetId);
    }
    openingPublicationIdRef.current = publicationId;
  };

  const renderOverviewContent = () => {
    if (effectiveBaseName === 'banking pools') return <OverviewBankingPoolsContent />;
    if (effectiveBaseName === 'full granularity') {
      if (effectiveReleaseSection === 'previous') {
        return <OverviewFullGranularityPreviousReleaseContent />;
      }
      return <OverviewFullGranularityContent />
    };
    if (effectiveBaseName === 'segment granularity') {
      if (effectiveReleaseSection === 'previous') {
        return <OverviewSegmentGranularityPreviousReleaseContent />;
      }
      return <OverviewSegmentGranularityContent />;
    }
    if (effectiveBaseName === 'total financial assets') {
      if (effectiveReleaseSection === 'previous') {
        return <OverviewTotalFinancialAssetsPreviousReleaseContent />;
      }
      return <OverviewTotalFinancialAssetsContent />;
    }
    return <OverviewUsBankingPoolsDefaultContent />;
  };

  const renderReleaseInformationContent = () => {
    if (effectiveBaseName === 'full granularity') {
      if (effectiveReleaseSection === 'previous') {
        return (
          <GgcPreviousReleaseInfoBlock
            introText={INTRO_29_MARKETS}
            metricsLine={METRICS_PREVIOUS_FULL_GRANULARITY}
            regions={<FullGranularityPreviousReleaseGeographicRegions />}
            previousCountryDetails={<FullGranularityPreviousCountryDetails />}
          />
        );
      }
      return (
        <GgcLatestAumReleaseInfoBlock
          introText={INTRO_6_MARKETS}
          metricsLine={METRICS_FULL_GRANULARITY}
          omitTrailingPeriodsOnGlobal
          regions={<FullGranularityGeographicRegions />}
          latestCountryDetails={<FullGranularityLatestCountryDetails />}
          globalHeading="Global / Overall"
        />
      );
    }
    if (effectiveBaseName === 'segment granularity') {
      if (effectiveReleaseSection === 'previous') {
        return (
          <GgcPreviousReleaseInfoBlock
            introText={INTRO_42_AUM_NET_FLOWS}
            metricsLine={METRICS_PREVIOUS_SEGMENT}
            regions={<FortyTwoMarketPreviousGeographicRegions />}
            previousCountryDetails={<SegmentGranularityPreviousCountryDetails />}
          />
        );
      }
      return (
        <GgcLatestAumReleaseInfoBlock
          introText={INTRO_LATEST_AUM_NET_FLOWS}
          metricsLine={METRICS_SEGMENT}
          omitTrailingPeriodsOnGlobal
          regions={<FortyTwoMarketGeographicRegionsSegmentLatest />}
          latestCountryDetails={<SegmentGranularityLatestCountryDetails />}
          globalHeading="Global/Overall"
        />
      );
    }
    if (effectiveBaseName === 'total financial assets') {
      if (effectiveReleaseSection === 'previous') {
        return (
          <GgcPreviousReleaseInfoBlock
            introText={INTRO_42_MARKETS_UPDATED}
            metricsLine={METRICS_TFA}
            regions={<FortyTwoMarketPreviousGeographicRegions />}
            previousCountryDetails={<TfaPreviousCountryDetails />}
          />
        );
      }
      return <TfaLatestReleaseInfoBlock />;
    }
    return (
      <>
        <p>Release Information</p>
      </>
    );
  };

  const renderVersions = () => (
    <ul className="dataset-info-versions-list">
      {versionsToShow.map((item) => {
        const pid = item.publicationId ?? item.id;
        const label = `${item.displayName ?? item.name} (${item.publicationStatus?.name})`;
        const created = formatPublicationCreatedAt(item.createdDate);
        const text = created ? `${label} - ${created}` : label;
        const idStr = `dataset-info-version-${pid}`;
        return (
          <li key={pid}>
            <label className="dataset-info-versions-option" htmlFor={idStr}>
              <input
                id={idStr}
                type="radio"
                name="dataset-info-version"
                checked={String(pendingPublicationId) === String(pid)}
                onChange={() => handleVersionPicked(pid)}
              />
              <span>{text}</span>
            </label>
          </li>
        );
      })}
    </ul>
  );

  const TAB_DEFS = [
    { id: TAB_OVERVIEW, label: 'Overview' },
    { id: TAB_RELEASE, label: 'Release Information' },
    { id: TAB_VERSIONS, label: 'Versions' },
  ];

  return (
    <div className="dataset-info-popup">
      <div className="dataset-info">
        <Icon
          type={TYPE_OUTLINE}
          name="lock-outline-16"
          src={Outline24ERemove}
          onClick={handleClose}
        />
        <div className="left-one">
          {visibleReleaseMenuItems.map((item, idx) => (
            <React.Fragment key={item.label}>
              {item.sectionHeading && (
                <h1 style={{ fontSize: 16, fontWeight: 700, padding: '16px 24px 4px', margin: 0, color: '#333' }}>
                  {item.sectionHeading}
                </h1>
              )}
              <button
                type="button"
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '10px 24px',
                  background: activeReleaseItemIdx === idx ? '#f0f0f0' : 'transparent',
                  border: 'none',
                  borderLeft: activeReleaseItemIdx === idx ? '4px solid #2251ff' : '4px solid transparent',
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: activeReleaseItemIdx === idx ? 600 : 400,
                  color: '#333'
                }}
                onClick={() => setActiveReleaseItemIdx(idx)}
              >
                {getReleaseInfoMenuItemDisplayLabel(item, allPublications)}
              </button>
            </React.Fragment>
          ))}
        </div>
        <div className="right-one">
          <h1 className="heading">
            {effectiveHeading}
          </h1>
          <div className="dataset-info-tabs" role="tablist" aria-label="Dataset details">
            {TAB_DEFS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                id={`dataset-info-tab-${tab.id}`}
                aria-selected={activeTab === tab.id}
                aria-controls={`dataset-info-panel-${tab.id}`}
                tabIndex={activeTab === tab.id ? 0 : -1}
                className={`dataset-info-tab ${activeTab === tab.id ? 'is-active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === TAB_OVERVIEW && (
            <div
              id={`dataset-info-panel-${TAB_OVERVIEW}`}
              role="tabpanel"
              aria-labelledby={`dataset-info-tab-${TAB_OVERVIEW}`}
              className="mds-p-5 dataset-info-overview"
            >
              <h1 className="sub-heading">Dataset Overview</h1>
              {renderOverviewContent()}
              <br />
            </div>
          )}

          {activeTab === TAB_RELEASE && (
            <div
              id={`dataset-info-panel-${TAB_RELEASE}`}
              role="tabpanel"
              aria-labelledby={`dataset-info-tab-${TAB_RELEASE}`}
              className="mds-p-5 dataset-info-release"
            >
              <h1 className="sub-heading">Release Information</h1>
              {renderReleaseInformationContent()}
              <br />
            </div>
          )}

          {activeTab === TAB_VERSIONS && (
            <div
              id={`dataset-info-panel-${TAB_VERSIONS}`}
              role="tabpanel"
              aria-labelledby={`dataset-info-tab-${TAB_VERSIONS}`}
              className="mds-p-5 dataset-info-versions"
            >
              <h1 className="sub-heading">Dataset Versions</h1>
              {/* <p style={{ marginBottom: 12, fontSize: 14, color: '#555' }}>
                Pick a version to load it immediately. You stay on this tab; the selected version also appears at the top of the main page.
              </p> */}
              {renderVersions()}
              <br />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DatasetInfoPopUp;
